import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import CopyClipboardButton from '@src/components/AdminHome/Common/CopyClipboardButton';
import { fetchWsFlowResponsesByFlowToken } from '@src/reducers/wsFlows';
import { getWsFlowResponsesByFlowToken } from '@src/selectors/wsFlows';
import { TimelineEvent } from '@src/types';
import dayjs from 'dayjs';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ChatMessageWSFlowTemplate from './ChatMessageWSFlowTemplate';

const GrayColor = '#F2F2F5';

interface IChatMessageWSFlowResponse {
  message: Partial<TimelineEvent>;
}

interface TabPanelProps {
  children: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <span>{children}</span>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    height: 224,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  tabContent: {
    display: 'block',
    fontSize: '14px',
    color: theme.palette.grey[500],
  },
}));

const AntTabs = withStyles({
  root: { paddingTop: '19px', minWidth: '125px', maxWidth: '300px' },
  indicator: {
    backgroundColor: GrayColor,
  },
  selected: {
    backgroundColor: GrayColor,
  },
})(Tabs);

const AntTab = withStyles(theme => ({
  root: {
    color: theme.palette.primary.main,
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'capitalize',
    height: '38px',
    minHeight: '38px',
    paddingLeft: '24px',
    whiteSpace: 'nowrap',
  },
  selected: {
    backgroundColor: GrayColor,
    color: theme.palette.text.primary,
  },
  wrapper: {
    alignItems: 'flex-start',
  },
}))(Tab);

const ChatMessageWSFlowResponse: React.FC<IChatMessageWSFlowResponse> = ({
  message,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);

  const dataResponse = useSelector(getWsFlowResponsesByFlowToken);

  const handleClick = () => {
    dispatch(
      fetchWsFlowResponsesByFlowToken({ flowToken: message.flowToken || '' })
    );
    setOpen(true);
  };

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number
  ) => {
    setValue(newValue);
  };

  if (!message.flowToken)
    return <ChatMessageWSFlowTemplate message={message} />;

  return (
    <>
      <Box width="100%">
        <Box fontSize="14px" fontWeight={500} width="100%">
          {formatMessage({ id: 'WhatsAppFlow.chatMessage.answered.text' })}
        </Box>
        <Divider style={{ marginTop: '7px' }} />
        <Box display="flex" textAlign="center" justifyContent="center">
          <Button style={{ color: '#478ECC' }} onClick={handleClick}>
            {formatMessage({
              id: 'WhatsAppFlow.chatMessage.see.button.text',
            })}
          </Button>
        </Box>
      </Box>
      <Dialog
        open={open}
        closeAfterTransition
        disableEscapeKeyDown
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ paddingLeft: '24px' }}>
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: formatMessage(
                { id: 'WhatsAppFlow.chatMessage.modal.title' },
                {
                  flowName: dataResponse?.flow_name || '',
                },
                { ignoreTag: true }
              ),
            }}
          />
          <Box fontSize={12} fontWeight={500}>
            {formatMessage(
              {
                id: 'WhatsAppFlow.chatMessage.modal.subtitle',
              },
              {
                date:
                  dayjs(dataResponse?.response_date).format(
                    'dddd, MMM DD, YYYY'
                  ) || '',
              }
            )}
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: '12px 24px 0px 0px' }}>
          <div className={classes.root}>
            <AntTabs
              orientation="vertical"
              variant="scrollable"
              value={value}
              onChange={handleChange}
              className={classes.tabs}
            >
              {dataResponse?.screens.map((screen, index) => (
                <AntTab
                  key={screen.screen_name}
                  label={screen.screen_name}
                  {...a11yProps(index)}
                />
              ))}
            </AntTabs>
            {dataResponse?.screens.map((screen, index) => (
              <TabPanel
                value={value}
                index={index}
                key={`tab-panel-${screen.screen_name}`}
              >
                {screen.field_responses.map(res => (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    key={`${res.name}-${res.value}`}
                  >
                    <span
                      className={classes.tabContent}
                    >{`${res.name}: ${res.value}`}</span>
                    <CopyClipboardButton
                      textoToClipboard={`${res.name}: ${res.value}`}
                      className="contact-item-copy"
                    />
                  </Box>
                ))}
              </TabPanel>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            color="primary"
            variant="contained"
          >
            {formatMessage({
              id: 'WhatsAppFlow.chatMessage.modal.goback.button.text',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatMessageWSFlowResponse;
